'use client';

import React from 'react';
import Link from 'next/link';
import { Heart, User, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLangDataStore } from '@/utils/util_states';
import { getLanguageDataByCode } from '@/i18n/language-data';
import type { Puzzle } from '@/utils/util_types';
import { formatPuzzleChatNum, getPuzzleAuthorName, getPuzzleDifficultyStars, isPuzzleNewThisWeek } from '@/utils/puzzle-display';

// ── Helpers ───────────────────────────────────────────

// ── Props ─────────────────────────────────────────────

type PuzzleCardProps = {
    puzzle: Puzzle;
    /** Zero-based index in the grid, used for staggered animation */
    index?: number;
    className?: string;
    onSelect?: (puzzle: Puzzle) => void;
    disabled?: boolean;
    href?: string;
    languageCode?: string;
};

// ── Component ─────────────────────────────────────────

export function PuzzleCard({ puzzle, index = 0, className, onSelect, disabled = false, href, languageCode }: PuzzleCardProps) {
    const storeLangData = useLangDataStore((state) => state.langData);
    const langData = languageCode ? getLanguageDataByCode(languageCode) : storeLangData;
    const difficultyStars = getPuzzleDifficultyStars(puzzle.difficulty);
    const chatCount = formatPuzzleChatNum(puzzle.chatNum);
    const authorName = getPuzzleAuthorName(puzzle, langData.puzzle_card_author_default);
    const animDelay = `${0.05 * (index + 1)}s`;
    const Root = (href ? Link : 'button') as unknown as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>;
    const rootProps = href
        ? {
            href,
            onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
                if (disabled) {
                    event.preventDefault();
                    return;
                }
                if (!onSelect || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                onSelect(puzzle);
            },
        }
        : {
            type: 'button',
            onClick: () => onSelect?.(puzzle),
            disabled,
        };

    return (
        <Root
            {...rootProps}
            aria-disabled={disabled || undefined}
            aria-busy={disabled}
            className={cn(
                'group relative flex flex-col gap-3 overflow-hidden',
                'w-full text-left bg-card border border-border p-6 cursor-pointer',
                'transition-all duration-300',
                'hover:border-lamp/15 hover:-translate-y-0.5',
                'hover:shadow-[0_8px_24px_rgba(0,0,0,0.3)]',
                disabled && 'opacity-60 cursor-wait',
                className,
            )}
            style={{
                animation: `fadeUp 0.4s ease-out ${animDelay} both`,
            }}
        >
            {/* Lamp glow overlay on hover */}
            <div
                className={cn(
                    'absolute -top-10 left-[30px] w-[100px] h-20 pointer-events-none',
                    'opacity-0 group-hover:opacity-100 transition-opacity duration-300',
                )}
                style={{
                    background: 'radial-gradient(ellipse, var(--lamp-glow) 0%, transparent 70%)',
                }}
            />

            {/* Top row: Title + Tag */}
            <div className="flex items-start gap-2.5 relative z-[1]">
                <h3 className="flex-1 text-[17px] font-bold leading-[1.4] text-card-foreground font-[family-name:var(--font-playfair)]">
                    {puzzle.title}
                </h3>
                <span
                    className={cn(
                        'shrink-0 text-[9px] font-semibold py-[3px] px-2 tracking-wider uppercase',
                        puzzle.origin_type === 'original'
                            ? 'text-lamp border border-lamp/25'
                            : 'text-muted-foreground border border-border',
                    )}
                >
                    {puzzle.origin_type === 'original'
                        ? langData.puzzle_card_original
                        : puzzle.origin_type === 'ai' ? 'AI' : langData.puzzle_card_repost}
                </span>
            </div>

            {/* Description */}
            <p
                className="text-[13px] text-muted-foreground leading-[1.7] flex-1 relative z-[1]"
                style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {puzzle.soup}
            </p>

            {/* Footer */}
            <div className="flex min-w-0 items-center justify-between gap-3 pt-3 border-t border-border relative z-[1]">
                <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                    {/* Author */}
                    <div className="flex min-w-0 shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <div
                            className={cn(
                                'h-[18px] w-[18px] rounded-full border border-border',
                                'flex items-center justify-center bg-background shrink-0',
                            )}
                        >
                            <User size={10} className="text-muted-foreground/60" />
                        </div>
                        <span className="max-w-[110px] truncate">{authorName}</span>
                    </div>

                    {/* New this week */}
                    {isPuzzleNewThisWeek(puzzle.created_at) && (
                        <span className="whitespace-nowrap border border-lamp/25 px-2 py-0.5 text-[9px] leading-none tracking-[0.5px] text-lamp/85">
                            {langData.puzzle_card_new_this_week}
                        </span>
                    )}

                    {/* Difficulty: five-star scale, matching the app */}
                    {difficultyStars > 0 && (
                        <span className="flex shrink-0 items-center gap-px leading-none" aria-label={`${difficultyStars}/5`}>
                            {Array.from({ length: 5 }, (_, index) => (
                                <span key={index} className={cn('text-[10px]', index < difficultyStars ? 'text-lamp' : 'text-lamp/20')}>★</span>
                            ))}
                        </span>
                    )}

                    {/* Participation count is intentionally hidden below 50 */}
                    {chatCount && (
                        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap border border-lamp/25 px-1.5 py-0.5 text-[9px] leading-none tracking-[0.5px] text-lamp/85">
                            <Users size={11} /> {chatCount} {langData.puzzle_card_solved_count}
                        </span>
                    )}
                </div>

                {/* Likes stay on the right, matching the app card */}
                {(puzzle.likedNum ?? 0) > 0 && (
                    <div className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground group-hover:text-muted-foreground/80 transition-colors">
                        <Heart size={14} />
                        <span>{puzzle.likedNum}</span>
                    </div>
                )}
            </div>
        </Root>
    );
}
